import { useCallback } from "react";
import type { ReactNode } from "react";
import { App } from "antd";
import { CloseCircleFilled, ExclamationCircleFilled } from "@ant-design/icons";

// Грешките от фискалния/ПОС принтера (няма хартия, отворен капак, няма връзка,
// timeout…) НЕ са обикновени toast-ове: операторът трябва да ги прочете и да
// отстрани причината, а модалът, от който е тръгнал печатът, често вече е
// затворен. Затова се показват през antd notification с duration: 0 — стоят,
// докато не се затворят ръчно с X. Нарочно НЕ е message с duration: 0:
// глобалният message конфиг (maxCount: 3 в main.tsx) би избутал висящата
// грешка при следващите toast-ове. Визуално обаче известието е компактният
// „pill" на message (икона + текст на един ред, без заглавие) — шаблонът със
// съдържание в `message` + Tailwind класове е като във VersionCheckNotifier.
// Всички останали съобщения в приложението остават 3-секундни toast-ове.

// Хваща целия pill: ширина по съдържанието (antd дава фиксирани 384px).
// НЕ w-auto: в стека wrapper-ът е shrink-to-fit и auto се свива до
// min-content (колонка по най-дългата дума); max-content + таванът дават
// един ред за кратък текст и пренасяне на 520px за дълъг.
const pillClassName =
  "!w-[max-content] !max-w-[min(520px,calc(100vw-24px))] !rounded-xl !py-[10px] !ps-4 !pe-10 " +
  "[&_.ant-notification-notice-message]:!mb-0 [&_.ant-notification-notice-message]:!pe-0 " +
  "[&_.ant-notification-notice-close]:!top-[11px] [&_.ant-notification-notice-close]:!end-3";

export function usePrinterNotifications() {
  const { notification } = App.useApp();

  const openPrinterNotice = useCallback(
    (icon: ReactNode, text: string) => {
      notification.open({
        message: (
          <div className="flex items-start gap-2.5">
            {icon}
            <span className="text-[14px] leading-[1.4] text-ink">{text}</span>
          </div>
        ),
        className: pillClassName,
        placement: "top",
        duration: 0,
      });
    },
    [notification],
  );

  // Категоричен отказ (status "failed"/HTTP 500) — червена икона.
  const notifyPrinterError = useCallback(
    (text: string) =>
      openPrinterNotice(
        <CloseCircleFilled className="mt-[2px] shrink-0 !text-[16px] !text-danger-ink" />,
        text,
      ),
    [openPrinterNotice],
  );

  // Неизвестен изход (status "timeout"/HTTP 504) — принтерът не е отговорил,
  // документът МОЖЕ и да е излязъл; кехлибарена икона вместо червена.
  const notifyPrinterTimeout = useCallback(
    (text: string) =>
      openPrinterNotice(
        <ExclamationCircleFilled className="mt-[2px] shrink-0 !text-[16px] !text-brand-ink" />,
        text,
      ),
    [openPrinterNotice],
  );

  return { notifyPrinterError, notifyPrinterTimeout };
}
